import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { ApiResponse } from '@card-software/library';
import {
  ComponentSummaryReportDto,
  ProcessSummaryReportDto,
  CostReportsProxyService,
  PeriodSummaryReportDto,
} from '../../../proxies/cost-reports.proxy.service';
import { CostSourceFilter } from '../models/cost-analysis.models';

type ApiObservable<T> = Observable<ApiResponse<T>>;

@Injectable({ providedIn: 'root' })
export class CostReportsCacheService {
  private readonly proxy = inject(CostReportsProxyService);

  private readonly periodSummaryCache = new Map<string, ApiObservable<PeriodSummaryReportDto>>();
  private readonly processSummaryCache = new Map<string, ApiObservable<ProcessSummaryReportDto>>();
  private readonly componentSummaryCache = new Map<string, ApiObservable<ComponentSummaryReportDto>>();

  getPeriodSummary(
    year: number,
    month: number,
    costSource: CostSourceFilter,
    onlyCompletedRolls = false,
    opts?: { refresh?: boolean },
  ): ApiObservable<PeriodSummaryReportDto> {
    return this.cached(this.periodSummaryCache, this.key(year, month, costSource, onlyCompletedRolls), opts, () =>
      this.proxy.getPeriodSummary(year, month, costSource, onlyCompletedRolls),
    );
  }

  getProcessSummary(
    year: number,
    month: number,
    costSource: CostSourceFilter,
    opts?: { refresh?: boolean },
  ): ApiObservable<ProcessSummaryReportDto> {
    return this.cached(this.processSummaryCache, this.key(year, month, costSource), opts, () =>
      this.proxy.getProcessSummaryReport(year, month, costSource),
    );
  }

  getComponentSummary(
    year: number,
    month: number,
    costSource: CostSourceFilter,
    opts?: { refresh?: boolean },
  ): ApiObservable<ComponentSummaryReportDto> {
    return this.cached(this.componentSummaryCache, this.key(year, month, costSource), opts, () =>
      this.proxy.getComponentSummaryReport(year, month, costSource),
    );
  }

  invalidatePeriod(year: number, month: number): void {
    const prefix = `${year}|${month}|`;
    for (const cache of [this.periodSummaryCache, this.processSummaryCache, this.componentSummaryCache]) {
      for (const k of [...cache.keys()]) {
        if (k.startsWith(prefix)) cache.delete(k);
      }
    }
  }

  hasPeriodSummary(year: number, month: number, costSource: CostSourceFilter, onlyCompletedRolls = false): boolean {
    return this.periodSummaryCache.has(this.key(year, month, costSource, onlyCompletedRolls));
  }

  invalidateAll(): void {
    this.periodSummaryCache.clear();
    this.processSummaryCache.clear();
    this.componentSummaryCache.clear();
  }

  private cached<T>(
    store: Map<string, ApiObservable<T>>,
    key: string,
    opts: { refresh?: boolean } | undefined,
    factory: () => ApiObservable<T>,
  ): ApiObservable<T> {
    if (opts?.refresh) store.delete(key);
    if (!store.has(key)) {
      store.set(key, factory().pipe(shareReplay({ bufferSize: 1, refCount: false })));
    }
    return store.get(key)!;
  }

  private key(year: number, month: number, costSource: CostSourceFilter, onlyCompletedRolls = false): string {
    return `${year}|${month}|${costSource}${onlyCompletedRolls ? '|completed' : ''}`;
  }
}
